import { computed, ref, watch } from 'vue';
import { useTimeoutFn } from '@vueuse/core';
import { useRoute } from 'vue-router/composables';
import { useAuthStore } from '@/composition/use-auth';
import { isTextEditable } from '@/composition/use-stable-window-size';
import { isGpthelpSupportedBrowser } from '@/gpthelp/gpthelp-support';
import { deepActiveElement } from '@/lib/dom/deep-active-element';
import { assistantPill } from './assistant-pill';
import { useAssistantStore } from './use-assistant-store';

/** The coachmark waits this long after the pill shows up (ms). */
const SHOW_DELAY_MS = 1500;

/**
 * Dialogs and open sheets own the screen: the coachmark waits for them.
 * Vuetify keeps a closed dialog's content node, `aria-modal` included, once
 * it has opened: its open state is `.v-dialog--active`.
 */
const OPEN_DIALOGS =
  '.vm--container, .v-dialog--active, [aria-modal="true"]:not(.v-dialog__content)';

function seenKey(userId: number): string {
  return `assistant.coachmark.${userId}`;
}

function readSeen(userId: number): boolean {
  try {
    return window.localStorage.getItem(seenKey(userId)) !== null;
  } catch {
    // Storage unavailable: it shows once per session instead.
    return false;
  }
}

function writeSeen(userId: number): void {
  try {
    window.localStorage.setItem(seenKey(userId), '1');
  } catch {
    // Storage unavailable: it shows once per session instead.
  }
}

function isShown(element: HTMLElement | null): element is HTMLElement {
  return (
    !!element && element.isConnected && element.getClientRects().length > 0
  );
}

/**
 * The «Su asistente» coachmark on the pill (plan §9.4): once per user, 1.5 s
 * after the pill shows up, only while no text field is focused and no dialog
 * is open (otherwise it tries again after the next route change). Its
 * buttons, a dismissal and any open of the panel mark it seen. Never on a
 * browser that cannot run the widget: «Probar» would open a panel that only
 * says so.
 */
export function useAssistantCoachmark() {
  const store = useAssistantStore();
  const authStore = useAuthStore();
  const route = useRoute();
  /** The browser can run the widget (it never changes at runtime). */
  const supported = isGpthelpSupportedBrowser();

  const visible = ref(false);
  /** Seen by the current user (or no user): it never shows. */
  const seen = ref(true);
  /** The last check found a field focused or a dialog open. */
  let retryOnRoute = false;

  const userId = computed(() => authStore.user?.id ?? null);
  /** The pill is up for a user who has not seen the coachmark yet. */
  const eligible = computed(
    () => supported && store.available && !seen.value && !!assistantPill.value
  );

  const { start, stop } = useTimeoutFn(showWhenCalm, SHOW_DELAY_MS, {
    immediate: false,
  });

  function canShowNow(): boolean {
    return (
      !store.open &&
      isShown(assistantPill.value) &&
      !isTextEditable(deepActiveElement()) &&
      !document.querySelector(OPEN_DIALOGS)
    );
  }

  function showWhenCalm() {
    if (!eligible.value || visible.value) return;
    retryOnRoute = !canShowNow();
    if (!retryOnRoute) visible.value = true;
  }

  function markSeen() {
    const id = userId.value;
    if (id !== null) writeSeen(id);
    seen.value = true;
    visible.value = false;
  }

  watch(
    userId,
    (id) => {
      visible.value = false;
      seen.value = id === null || readSeen(id);
    },
    { immediate: true }
  );

  watch(
    eligible,
    (isEligible) => {
      retryOnRoute = false;
      if (isEligible) {
        start();
        return;
      }
      stop();
      visible.value = false;
    },
    { immediate: true }
  );

  watch(
    () => route.fullPath,
    () => {
      if (!retryOnRoute || !eligible.value) return;
      retryOnRoute = false;
      start();
    }
  );

  // Opening the panel in any way counts as seen.
  watch(
    () => store.open,
    (open) => {
      if (open && !seen.value) markSeen();
    }
  );

  /**
   * The popover closed itself: Esc or a tap elsewhere dismiss it; a pill
   * that stopped showing (another route) only hides it for now.
   */
  function onClosed() {
    if (store.available && isShown(assistantPill.value)) markSeen();
    else visible.value = false;
  }

  function tryIt() {
    markSeen();
    store.openPanel('coachmark');
  }

  return {
    visible,
    anchor: assistantPill,
    dismiss: markSeen,
    tryIt,
    onClosed,
  };
}
